import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  CheckCircle,
  Schedule,
  AssignmentTurnedIn,
  Speed,
  Timer,
} from '@mui/icons-material';
import { Task, TaskStatus, TaskPriority } from '../../types/common';
import { Sprint } from '../../entities/sprint/model/types';
import './ProgressDashboard.scss';

interface ProgressDashboardProps {
  tasks: Task[];
  activeSprint?: Sprint | null;
}

const ProgressDashboard: React.FC<ProgressDashboardProps> = ({ tasks, activeSprint }) => {
  // Calculate metrics
  const getTotalTasks = () => tasks.length;

  const getTasksByStatus = (status: TaskStatus) =>
    tasks.filter((task) => task.status === status).length;

  const getTasksByPriority = (priority: TaskPriority) =>
    tasks.filter((task) => task.priority === priority).length;

  const getCompletionRate = () => {
    const total = getTotalTasks();
    if (total === 0) return 0;
    const completed = getTasksByStatus(TaskStatus.DONE);
    return (completed / total) * 100;
  };

  const getTotalEstimatedHours = () =>
    tasks.reduce((sum, task) => sum + (task.estimatedHours || 0), 0);

  const getTotalActualHours = () =>
    tasks.reduce((sum, task) => sum + (task.actualHours || 0), 0);

  const getOverdueTasks = () => {
    const now = new Date();
    return tasks.filter((task) => {
      if (!task.dueDate || task.status === TaskStatus.DONE) return false;
      return new Date(task.dueDate) < now;
    }).length;
  };

  const getSprintProgress = () => {
    if (!activeSprint || !activeSprint.totalStoryPoints) return 0;
    return ((activeSprint.completedStoryPoints || 0) / activeSprint.totalStoryPoints) * 100;
  };

  const getSprintDaysRemaining = () => {
    if (!activeSprint) return 0;
    const end = new Date(activeSprint.endDate);
    const today = new Date();
    return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const StatCard = ({
    title,
    value,
    icon,
    color,
    subtitle,
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
  }) => (
    <Card className="stat-card">
      <CardContent>
        <Box className="stat-card__header">
          <Box className="stat-card__icon" style={{ background: color }}>
            {icon}
          </Box>
          <Typography variant="h4" className="stat-card__value">
            {value}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" className="stat-card__title">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box className="progress-dashboard">
      <Typography variant="h5" className="progress-dashboard__title">
        Project Progress Overview
      </Typography>

      {/* Key Metrics */}
      <Box
        className="progress-dashboard__metrics"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
        }}
      >
        <StatCard
          title="Total Tasks"
          value={getTotalTasks()}
          icon={<AssignmentTurnedIn />}
          color="linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)"
        />
        <StatCard
          title="Completed"
          value={getTasksByStatus(TaskStatus.DONE)}
          icon={<CheckCircle />}
          color="linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)"
          subtitle={`${getCompletionRate().toFixed(1)}% complete`}
        />
        <StatCard
          title="In Progress"
          value={getTasksByStatus(TaskStatus.IN_PROGRESS)}
          icon={<TrendingUp />}
          color="linear-gradient(135deg, #f39c12 0%, #f1c40f 100%)"
        />
        <StatCard
          title="Overdue"
          value={getOverdueTasks()}
          icon={<Schedule />}
          color="linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)"
        />
      </Box>

      {/* Sprint Progress */}
      {activeSprint && (
        <Card className="progress-dashboard__sprint">
          <CardContent>
            <Box className="progress-dashboard__sprint-header">
              <Typography variant="h6">Active Sprint: {activeSprint.name}</Typography>
              <Chip
                label={`${getSprintDaysRemaining()} days left`}
                color="primary"
                size="small"
              />
            </Box>

            {activeSprint.goal && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Goal: {activeSprint.goal}
              </Typography>
            )}

            <Box className="progress-dashboard__sprint-stats">
              <Box className="progress-stat">
                <Typography variant="caption" color="text.secondary">
                  Story Points
                </Typography>
                <Typography variant="h6">
                  {activeSprint.completedStoryPoints || 0} / {activeSprint.totalStoryPoints || 0}
                </Typography>
              </Box>
              {activeSprint.velocity && (
                <Box className="progress-stat">
                  <Speed fontSize="small" />
                  <Typography variant="body2">
                    Velocity: {activeSprint.velocity} SP
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Box className="progress-dashboard__progress-header">
                <Typography variant="body2">Progress</Typography>
                <Typography variant="body2">{getSprintProgress().toFixed(1)}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getSprintProgress()}
                className="progress-dashboard__progress-bar"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Status Breakdown */}
      <Card className="progress-dashboard__breakdown">
        <CardContent>
          <Typography variant="h6" className="progress-dashboard__section-title">
            Task Status Breakdown
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {[
              { status: TaskStatus.TODO, label: 'To Do', color: '#6c757d' },
              { status: TaskStatus.IN_PROGRESS, label: 'In Progress', color: '#4A90E2' },
              { status: TaskStatus.REVIEW, label: 'Review', color: '#f39c12' },
              { status: TaskStatus.TESTING, label: 'Testing', color: '#9b59b6' },
              { status: TaskStatus.DONE, label: 'Done', color: '#27ae60' },
              { status: TaskStatus.BLOCKED, label: 'Blocked', color: '#e74c3c' },
            ].map(({ status, label, color }) => {
              const count = getTasksByStatus(status);
              const percentage = getTotalTasks() > 0 ? (count / getTotalTasks()) * 100 : 0;

              return (
                <Box key={status}>
                  <Box className="status-item">
                    <Box className="status-item__header">
                      <Box className="status-item__label">
                        <Box
                          className="status-item__dot"
                          style={{ backgroundColor: color }}
                        />
                        <Typography variant="body2">{label}</Typography>
                      </Box>
                      <Typography variant="h6">{count}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        mt: 1,
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#e9ecef',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: color,
                        },
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Priority Distribution */}
      <Card className="progress-dashboard__breakdown">
        <CardContent>
          <Typography variant="h6" className="progress-dashboard__section-title">
            Priority Distribution
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {[
              { priority: TaskPriority.URGENT, label: 'Urgent', color: '#e74c3c' },
              { priority: TaskPriority.HIGH, label: 'High', color: '#f39c12' },
              { priority: TaskPriority.MEDIUM, label: 'Medium', color: '#4A90E2' },
              { priority: TaskPriority.LOW, label: 'Low', color: '#95a5a6' },
            ].map(({ priority, label, color }) => {
              const count = getTasksByPriority(priority);
              const percentage = getTotalTasks() > 0 ? (count / getTotalTasks()) * 100 : 0;

              return (
                <Box key={priority}>
                  <Box className="priority-item">
                    <Typography variant="h4" style={{ color }}>
                      {count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Time Tracking Summary */}
      <Card className="progress-dashboard__time">
        <CardContent>
          <Typography variant="h6" className="progress-dashboard__section-title">
            Time Tracking Summary
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3 }}>
            <Box className="time-stat">
              <Timer className="time-stat__icon" />
              <Typography variant="h5">{getTotalEstimatedHours()}h</Typography>
              <Typography variant="body2" color="text.secondary">
                Estimated Hours
              </Typography>
            </Box>
            <Box className="time-stat">
              <Timer className="time-stat__icon" />
              <Typography variant="h5">{getTotalActualHours()}h</Typography>
              <Typography variant="body2" color="text.secondary">
                Actual Hours
              </Typography>
            </Box>
            <Box className="time-stat">
              <TrendingUp className="time-stat__icon" />
              <Typography variant="h5">
                {getTotalEstimatedHours() > 0
                  ? ((getTotalActualHours() / getTotalEstimatedHours()) * 100).toFixed(1)
                  : 0}
                %
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Time Efficiency
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProgressDashboard;
